<div class="docs-home">
  <div class="docs-toolbar">
    <div class="xr-filters" role="group" [attr.aria-label]="'filemanager.home.filter_label' | translate">
      @for (option of filters; track option) {
        <button type="button" class="xr-filter" [class.active]="filter === option" [attr.aria-pressed]="filter === option"
                (click)="setFilter(option)">
          {{ 'filemanager.home.filter_' + option | translate }}
        </button>
      }
    </div>
    <button type="button" class="xr-btn xr-btn--secondary" (click)="browseAll.emit()">
      <i-tabler name="folders" class="icon-18" aria-hidden="true"></i-tabler>
      {{ 'filemanager.home.manage' | translate }}
    </button>
  </div>

  @if (filter === 'range') {
    <div class="docs-range">
      <label>
        <span>{{ 'filemanager.home.from' | translate }}</span>
        <input type="date" [(ngModel)]="from">
      </label>
      <label>
        <span>{{ 'filemanager.home.to' | translate }}</span>
        <input type="date" [(ngModel)]="to">
      </label>
    </div>
  }

  @if (loading) {
    <div class="docs-loading"><mat-spinner diameter="34"></mat-spinner></div>
  } @else if (failed) {
    <div class="xr-empty">
      <p>{{ 'filemanager.home.load_error' | translate }}</p>
      <button type="button" class="xr-btn xr-btn--secondary" (click)="load()">{{ 'conversations.retry' | translate }}</button>
    </div>
  } @else {
    @if (filteredOut) {
      <div class="xr-empty">{{ 'filemanager.home.nothing_in_range' | translate }}</div>
    }

    <!-- Call conversations: one row per day, newest first -->
    <section class="xr-card docs-section">
      <header class="docs-section-head">
        <span class="docs-section-icon docs-section-icon--green"><i-tabler name="message-circle" class="icon-20"></i-tabler></span>
        <div>
          <h2 class="xr-card-title">{{ 'filemanager.home.conversations' | translate }}</h2>
          <p>{{ 'filemanager.home.conversations_help' | translate }}</p>
        </div>
      </header>
      @for (day of visibleConversationDays; track day.folder.id) {
        <button type="button" class="docs-row docs-day" (click)="toggleDay(day)" [attr.aria-expanded]="day.open">
          <i-tabler [name]="day.open ? 'chevron-down' : 'chevron-right'" class="icon-18 docs-muted"></i-tabler>
          <i-tabler name="folder-filled" class="icon-20 docs-folder"></i-tabler>
          <span class="docs-row-main"><strong>{{ dayLabel(day.day) }}</strong></span>
          @if (day.files) {
            <span class="docs-muted">{{ day.files.length }} {{ (day.files.length === 1 ? 'filemanager.home.conversation' : 'filemanager.home.conversations_count') | translate }}</span>
          }
        </button>
        @if (day.open) {
          @if (day.loading) {
            <div class="docs-loading docs-loading--small"><mat-spinner diameter="22"></mat-spinner></div>
          }
          @for (file of day.files ?? []; track file.id) {
            <div class="docs-row docs-file docs-file--nested" role="button" tabindex="0" (click)="view(file)" (keydown.enter)="view(file)">
              <span class="docs-kind docs-kind--pdf">PDF</span>
              <span class="docs-row-main">
                <strong>{{ conversationTitle(file) }}</strong>
                <small>{{ size(file.dimension_file) }}</small>
              </span>
              @if (aiKey(file); as key) {
                <span class="xr-badge docs-hide-phone" [ngClass]="aiClass(file)">{{ key | translate }}</span>
              }
              <button type="button" class="xr-btn xr-btn--soft xr-btn--small" (click)="download(file, $event)"
                      [attr.aria-label]="'filemanager.home.download' | translate">
                <i-tabler name="download" class="icon-16"></i-tabler>
              </button>
            </div>
          }
        }
      } @empty {
        <p class="docs-section-empty">{{ 'filemanager.home.no_conversations' | translate }}</p>
      }
    </section>

    <!-- The operator's own folders and documents: folders first, then files, A→Z -->
    <section class="xr-card docs-section">
      <header class="docs-section-head">
        <span class="docs-section-icon docs-section-icon--navy"><i-tabler name="file-text" class="icon-20"></i-tabler></span>
        <div>
          <h2 class="xr-card-title">{{ 'filemanager.home.manuals' | translate }}</h2>
          <p>{{ 'filemanager.home.manuals_help' | translate }}</p>
        </div>
      </header>
      @for (item of visibleManuals; track item.id) {
        @if (item.type === 'folder') {
          <button type="button" class="docs-row docs-file" (click)="openFolder.emit(item.id)">
            <span class="docs-kind docs-kind--folder"><i-tabler name="folder-filled" class="icon-18"></i-tabler></span>
            <span class="docs-row-main">
              <strong>{{ item.name }}</strong>
              <small>{{ 'filemanager.home.folder' | translate }} · {{ shortDate(item.updated_at || item.created_at) }}</small>
            </span>
            <i-tabler name="chevron-right" class="icon-18 docs-muted"></i-tabler>
          </button>
        } @else {
          <div class="docs-row docs-file" role="button" tabindex="0" (click)="view(item)" (keydown.enter)="view(item)">
            <span class="docs-kind" [ngClass]="'docs-kind--' + kind(item)">{{ kind(item) === 'pdf' ? 'PDF' : 'IMG' }}</span>
            <span class="docs-row-main">
              <strong>{{ item.name }}</strong>
              <small>{{ size(item.dimension_file) }} · {{ shortDate(item.created_at) }}</small>
            </span>
            @if (aiKey(item); as key) {
              <span class="xr-badge docs-hide-phone" [ngClass]="aiClass(item)">{{ key | translate }}</span>
            }
            <button type="button" class="xr-btn xr-btn--soft xr-btn--small" (click)="download(item, $event)"
                    [attr.aria-label]="'filemanager.home.download' | translate">
              <i-tabler name="download" class="icon-16"></i-tabler>
            </button>
          </div>
        }
      } @empty {
        <p class="docs-section-empty">{{ 'filemanager.home.no_manuals' | translate }}</p>
      }
    </section>

    <!-- Photos from the video and images sent in calls, by day -->
    <section class="xr-card docs-section">
      <header class="docs-section-head">
        <span class="docs-section-icon docs-section-icon--blue"><i-tabler name="camera" class="icon-20"></i-tabler></span>
        <div>
          <h2 class="xr-card-title">{{ 'filemanager.home.photos' | translate }}</h2>
          <p>{{ 'filemanager.home.photos_help' | translate }}</p>
        </div>
      </header>
      <div class="docs-photo-days">
        @for (group of visiblePhotoDays; track group.day) {
          <div class="docs-photo-day">
            <h3>{{ dayLabel(group.day) }} · {{ group.photos.length }}</h3>
            <div class="docs-photo-grid">
              @for (photo of group.photos; track photo.id) {
                <button type="button" class="docs-photo" (click)="view(photo)">
                  <span class="docs-photo-thumb"><i-tabler name="photo" class="icon-28"></i-tabler></span>
                  <strong>{{ photoTitle(photo) }}</strong>
                  <small>{{ size(photo.dimension_file) }}</small>
                </button>
              }
            </div>
          </div>
        } @empty {
          <p class="docs-section-empty">{{ 'filemanager.home.no_photos' | translate }}</p>
        }
      </div>
    </section>
  }
</div>
